import './assets/main.css'

import { createApp } from 'vue'
import { globalCookiesConfig } from 'vue3-cookies'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'
import PrimeVue from 'primevue/config'
import Aura from '@primevue/themes/aura'
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionPanel,
  Badge,
  Button,
  Card,
  Checkbox,
  Chip,
  Column,
  ConfirmDialog,
  ConfirmationService,
  DataTable,
  DataView,
  DatePicker,
  Dialog,
  Divider,
  Fieldset,
  Image,
  InputNumber,
  InputText,
  Listbox,
  Menubar,
  Message,
  OverlayBadge,
  Popover,
  ProgressSpinner,
  Rating,
  Select,
  SelectButton,
  Skeleton,
  Splitter,
  SplitterPanel,
  Step,
  StepList,
  StepPanel,
  StepPanels,
  Stepper,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Textarea,
  Toast,
  ToastService,
  ToggleSwitch,
  Tooltip,
} from 'primevue'
import { datadogLogs } from '@datadog/browser-logs'
import App from './App.vue'
import router from './router'

import './assets/main.css'

if (!!import.meta.env.VITE_DATADOG_CLIENT_TOKEN) {
  datadogLogs.init({
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.eu',
    service: 'customer-care',
    forwardErrorsToLogs: false,
    sampleRate: 100,
    trackUserInteractions: true,
  })
}

const pinia = createPinia()
const app = createApp(App)

if (!!import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [Sentry.captureConsoleIntegration({ levels: ['error'] })],
  })
  pinia.use(Sentry.createSentryPiniaPlugin())
}

app.use(pinia)
app.use(router)
app.use(PrimeVue, {
  theme: {
    preset: Aura,
    options: {
      darkModeSelector: false || 'none',
      cssLayer: {
        name: 'primevue',
        order: 'tailwind-base, primevue, tailwind-utilities',
      },
    },
  },
  locale: {
    dayNames: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
    dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
    dayNamesMin: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
    monthNames: [
      'Janvier',
      'Février',
      'Mars',
      'Avril',
      'Mai',
      'Juin',
      'Juillet',
      'Août',
      'Septembre',
      'Octobre',
      'Novembre',
      'Décembre',
    ],
    monthNamesShort: [
      'Jan',
      'Fév',
      'Mar',
      'Avr',
      'Mai',
      'Juin',
      'Juil',
      'Aoû',
      'Sep',
      'Oct',
      'Nov',
      'Déc',
    ],
    firstDayOfWeek: 1,
  },
})
app.use(ToastService)
app.use(ConfirmationService)
app.component('Accordion', Accordion)
app.component('AccordionContent', AccordionContent)
app.component('AccordionHeader', AccordionHeader)
app.component('AccordionPanel', AccordionPanel)
app.component('Badge', Badge)
app.component('Button', Button)
app.component('Card', Card)
app.component('Checkbox', Checkbox)
app.component('Chip', Chip)
app.component('ConfirmDialog', ConfirmDialog)
app.component('Column', Column)
app.component('DataTable', DataTable)
app.component('DataView', DataView)
app.component('DatePicker', DatePicker)
app.component('Dialog', Dialog)
app.component('Divider', Divider)
app.component('Fieldset', Fieldset)
app.component('Image', Image)
app.component('InputNumber', InputNumber)
app.component('InputText', InputText)
app.component('Listbox', Listbox)
app.component('Menubar', Menubar)
app.component('Message', Message)
app.component('OverlayBadge', OverlayBadge)
app.component('Popover', Popover)
app.component('ProgressSpinner', ProgressSpinner)
app.component('Rating', Rating)
app.component('Select', Select)
app.component('SelectButton', SelectButton)
app.component('Skeleton', Skeleton)
app.component('Splitter', Splitter)
app.component('SplitterPanel', SplitterPanel)
app.component('Step', Step)
app.component('StepList', StepList)
app.component('StepPanel', StepPanel)
app.component('StepPanels', StepPanels)
app.component('Stepper', Stepper)
app.component('Tab', Tab)
app.component('TabList', TabList)
app.component('TabPanel', TabPanel)
app.component('TabPanels', TabPanels)
app.component('Tabs', Tabs)
app.component('Tag', Tag)
app.component('Textarea', Textarea)
app.component('Toast', Toast)
app.component('ToggleSwitch', ToggleSwitch)

app.directive('tooltip', Tooltip)

app.mount('#app')
